import { openedSessionTabAtom, openedSessionTabEnum } from '@/atoms/sideNavAtoms';
import { selectedReviewMemberAtom } from '@/atoms/teamAtoms';
import { SessionCreationInProgressBanner } from '@/features/sessions/components/session-creation-in-progress-banner';
import { ACTIONS, PERMISSION_SUBJECTS, ability } from '@/helpers/casl/ability';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useShowSessionReview } from '@/hooks/useShowSessionReview';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { isOnPrem } from '@/utils/buildProfile';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@heidi/design-system/tabs';
import { TypographyCaptionBold, TypographyP } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtom, useAtomValue } from 'jotai';
import { memo, useMemo } from 'react';
import SessionBarHeader from './header/header';
import { useSessionsFilteredList } from './hooks/useSessionsFilteredList';
import SessionBarUpcomingTab from './session-bar-upcoming-tab';
import { useRetrieveAppointmentOrSchedule } from './side-nav-list/integration/hooks/useRetrieveAppointmentOrSchedule';
import { SideNavSessionsList } from './side-nav-list/side-nav-sessions-list';

const SessionsBar = memo(() => {
  const { t } = useTranslations();
  const [openedSessionTab, setOpenedSessionTab] = useAtom(openedSessionTabAtom);
  const selectedReviewUser = useAtomValue(selectedReviewMemberAtom);
  const isMobile = useIsMobile();
  const { data: userKindeId } = useQueryUser((data) => data?.kinde_user_id);
  const { searchResult: ehrSearchResult } = useRetrieveAppointmentOrSchedule();
  const { sessionSearchResult, searchValue, totalMatchCount } = useSessionsFilteredList();
  const showSessionReview = useShowSessionReview();

  const height = useMemo(() => {
    if (isMobile) {
      return 'calc(100% - 128px)';
    }

    return 'calc(100% - 62px)';
  }, [isMobile]);

  return (
    <div className="flex size-full w-full flex-col gap-2 sm:gap-1">
      <SessionBarHeader />

      {/* tabs for sessions */}
      <Tabs
        className="w-full transition-all ease-in-out"
        value={openedSessionTab}
        onValueChange={(e) => {
          setOpenedSessionTab(e as openedSessionTabEnum);
        }}
        style={{
          height,
        }}
      >
        <TabsList className="h-fit w-full bg-transparent py-0">
          {/* The Schedule (Upcoming) tab depends on EHR/calendar integrations that
              aren't available on-prem, so it's hidden there — the default tab is
              already `PAST` (see `openedSessionTabAtom`). */}
          {!isOnPrem && (
            <TabsTrigger
              value={openedSessionTabEnum.UPCOMING}
              className="w-full rounded-none border-b-2 border-outline hover:text-text-primary data-[state=active]:border-border-selected data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              disabled={
                (showSessionReview && selectedReviewUser !== userKindeId) ||
                ability.cannot(ACTIONS.ACCESS, PERMISSION_SUBJECTS.SESSION_VIEW)
              }
            >
              {isMobile ? (
                <TypographyP>{t('sessions.schedule')}</TypographyP>
              ) : (
                <TypographyCaptionBold>
                  {t('sessions.schedule')}{' '}
                  {searchValue && ehrSearchResult && `(${ehrSearchResult.length})`}
                </TypographyCaptionBold>
              )}
            </TabsTrigger>
          )}
          <TabsTrigger
            value={openedSessionTabEnum.PAST}
            className="w-full rounded-none border-b-2 border-outline hover:text-text-primary data-[state=active]:border-border-selected data-[state=active]:bg-transparent data-[state=active]:shadow-none"
          >
            {isMobile ? (
              <TypographyP>{t('sessions.past')}</TypographyP>
            ) : (
              <TypographyCaptionBold>
                {t('sessions.past')}{' '}
                {searchValue &&
                  (totalMatchCount !== undefined
                    ? `(${totalMatchCount})`
                    : sessionSearchResult && `(${sessionSearchResult.length})`)}
              </TypographyCaptionBold>
            )}
          </TabsTrigger>
        </TabsList>
        {!isOnPrem && (
          <TabsContent
            value={openedSessionTabEnum.UPCOMING}
            style={{ height: isMobile ? 'calc(100% - 44px)' : 'calc(100% - 36px)' }}
            className="mt-1.5"
          >
            <SessionBarUpcomingTab />
          </TabsContent>
        )}
        <TabsContent
          value={openedSessionTabEnum.PAST}
          style={{ height: isMobile ? 'calc(100% - 44px)' : 'calc(100% - 6px)' }}
          className="mt-1.5 flex flex-col"
        >
          <SessionCreationInProgressBanner />
          <div className="min-h-0 flex-1">
            <SideNavSessionsList />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
});

SessionsBar.displayName = 'SessionsBar';
export default SessionsBar;
